/**
 * School- and public-holiday logic. Pure, no React, no data access — the calendar
 * bands and the Edge Function that fills the mirror both run this code.
 *
 * WHY the product cares: a landlord in Tyrol prices the second week of February by
 * whether Bavaria, Baden-Württemberg or the Dutch "regio Zuid" is off school that
 * week. Three ministries, three calendars, none of them memorable.
 *
 * DATA LICENCE — read before touching any UI that renders this:
 * the source is the OpenHolidays API and the data is ODbL. Commercial use is
 * permitted, **attribution is required**. Use `HOLIDAY_ATTRIBUTION` below; it is not
 * decoration, it is the licence condition.
 *
 * THE ONE TRAP IN THIS FILE. Two date conventions meet here and they disagree:
 *
 *   * OpenHolidays `endDate` is INCLUSIVE — it is the last day off school.
 *   * StaySuite ranges are half-open `[from, to)`, like every booking in the schema.
 *
 * A summer holiday of `2026-07-04 … 2026-08-16` is `[2026-07-04, 2026-08-17)`. Get
 * that wrong and the calendar drops the last day of every holiday — the Sunday that
 * is very often the departure day, and therefore exactly the night being priced.
 * `toPeriod()` does the conversion, and there is a test for the off-by-one.
 *
 * This module deliberately imports NOTHING, not even `./dates`: a byte-identical copy
 * is shipped into the `holiday-sync` Deno bundle, which cannot reach into the Vite
 * source tree. Keeping it dependency-free is what makes "the deployed parser is the
 * tested parser" true rather than aspirational.
 */

/** `YYYY-MM-DD`. Structurally the same as `ISODate` in `./dates`; see the note above. */
export type ISODate = string

export const HOLIDAY_SOURCE_URL = 'https://openholidaysapi.org'

export const HOLIDAY_ATTRIBUTION =
  'Holiday data: OpenHolidays API (openholidaysapi.org), licensed ODbL.'

export type HolidayKind = 'public' | 'school'
export type HolidayRegionKind = 'subdivision' | 'group' | 'nationwide'

/** The sentinel `region_code` for a holiday that applies to a whole country. */
export const NATIONWIDE = '*'

/** `{ de: 'Sommerferien', en: 'Summer holidays' }`, keyed by lower-case language. */
export type LocalisedName = Record<string, string>

// --------------------------------------------------------------- the API's shape

export interface OpenHolidaysText { language: string; text: string }
export interface OpenHolidaysRef { code: string; shortName?: string; name?: OpenHolidaysText[] }

/**
 * One record as OpenHolidays delivers it. `subdivisions`, `groups` and neither are all
 * real: Czech summer holidays are nationwide and carry no region at all, German school
 * holidays carry subdivisions, Belgian ones carry groups.
 */
export interface OpenHolidaysRecord {
  id: string
  startDate: ISODate
  /** INCLUSIVE. The last day of the holiday. */
  endDate: ISODate
  type?: string
  name: OpenHolidaysText[]
  nationwide?: boolean
  subdivisions?: OpenHolidaysRef[]
  groups?: OpenHolidaysRef[]
}

// ------------------------------------------------------------- the mirror's shape

/** One row of the `holidays` table. Mirrors the SQL exactly. */
export interface HolidayRow {
  source_id: string
  country_code: string
  region_code: string
  region_kind: HolidayRegionKind
  region_name: LocalisedName
  kind: HolidayKind
  name: LocalisedName
  start_date: ISODate
  /** INCLUSIVE, stored verbatim from the API. */
  end_date: ISODate
  /** Generated column: `end_date + 1`. Present on reads, absent on writes. */
  end_date_excl?: ISODate
  nationwide: boolean
  synced_at?: string
}

/** A row of `tenant_holidays()`, which adds the tenant's reason for watching it. */
export interface TenantHolidayRow extends HolidayRow {
  role: HolidayRegionRole
}

export type HolidayRegionRole = 'home' | 'market'

/** A row of `tenant_holiday_regions`. */
export interface TenantHolidayRegion {
  country_code: string
  region_code: string
  role: HolidayRegionRole
  colour?: string | null
  sort_order: number
}

/** A row of `tenant_holiday_coverage()`. */
export interface HolidayCoverage {
  country_code: string
  region_code: string
  region_name: LocalisedName
  role: HolidayRegionRole
  kind: HolidayKind
  records: number
  first_start: ISODate | null
  /** Last day anything is published for. `null` means nothing at all is published. */
  published_through: ISODate | null
}

// ------------------------------------------------------------- the calendar's shape

/**
 * A holiday as the rest of the app should handle it: half-open, like every other range
 * in this codebase. `to` is the day AFTER the holiday ends.
 */
export interface HolidayPeriod {
  sourceId: string
  country: string
  region: string
  regionName: LocalisedName
  kind: HolidayKind
  name: LocalisedName
  from: ISODate
  /** EXCLUSIVE. `[from, to)`. */
  to: ISODate
  role?: HolidayRegionRole
}

// -------------------------------------------------------------------- date helpers

/**
 * UTC arithmetic on purpose. A calendar date has no time zone, and doing this at UTC
 * midnight with UTC accessors cannot be shifted by a DST boundary — which is the same
 * guarantee `./dates` gets by staying in local midnight, reached from the other side.
 */
export function addDaysISO(iso: ISODate, days: number): ISODate {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Inclusive end date -> exclusive upper bound. The whole point of this file. */
export function exclusiveEnd(inclusiveEndDate: ISODate): ISODate {
  return addDaysISO(inclusiveEndDate, 1)
}

/** Exclusive upper bound -> inclusive end date, for display ("… bis 16. August"). */
export function inclusiveEnd(exclusiveTo: ISODate): ISODate {
  return addDaysISO(exclusiveTo, -1)
}

/** Days covered by an inclusive `start … end` pair. A one-day holiday is 1, not 0. */
export function inclusiveDayCount(start: ISODate, endInclusive: ISODate): number {
  const ms = Date.parse(`${endInclusive}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)
  return Math.round(ms / 86_400_000) + 1
}

/**
 * The OpenHolidays date range is capped at three years per query. Splits an inclusive
 * `[from, to]` into windows that stay inside it.
 *
 * Both bounds are inclusive here because that is what the API's `validFrom`/`validTo`
 * mean — this is the one place in the codebase that does not use `[from, to)`, and it
 * is because we are speaking someone else's protocol.
 */
export function chunkRange(from: ISODate, to: ISODate, maxDays = 365 * 3 - 1): Array<[ISODate, ISODate]> {
  const out: Array<[ISODate, ISODate]> = []
  if (to < from) return out
  let start = from
  while (start <= to) {
    const end = addDaysISO(start, maxDays)
    const stop = end < to ? end : to
    out.push([start, stop])
    start = addDaysISO(stop, 1)
  }
  return out
}

// ------------------------------------------------------------------------ parsing

export function localiseName(texts: OpenHolidaysText[] | undefined): LocalisedName {
  const out: LocalisedName = {}
  for (const t of texts ?? []) out[t.language.toLowerCase()] = t.text
  return out
}

/**
 * Picks a display name. Falls back through the requested locale, German, English, then
 * whatever the record happens to carry — a Polish-only name beats an empty string.
 */
export function holidayName(name: LocalisedName, locale = 'de'): string {
  const lang = locale.slice(0, 2).toLowerCase()
  return name[lang] ?? name['de'] ?? name['en'] ?? Object.values(name)[0] ?? ''
}

export interface ParseOptions {
  country: string
  kind: HolidayKind
  /** Display names by region code, from `/Subdivisions` and `/Groups`. */
  regionNames?: Map<string, LocalisedName>
  /**
   * Set when the record was fetched with `groupCode=…`. It WINS over the record's own
   * `subdivisions`, because a group-filtered Dutch response still lists every one of
   * the ~350 municipalities in that region — attributing by those would write 350
   * useless rows for a holiday the caller asked about by region.
   */
  queriedGroup?: string
  syncedAt?: string
}

/**
 * Fans one API record out to one row per region it applies to.
 *
 * Order matters: an explicit group query first, then `subdivisions`, then `groups`,
 * then nationwide. German records can carry BOTH `subdivisions: [DE-MV]` and
 * `groups: [DE-MV-ABS]` (school types with different dates) — subdivisions is the
 * right level to price on, and the distinct source ids keep both periods.
 */
export function parseHolidayRecord(rec: OpenHolidaysRecord, opts: ParseOptions): HolidayRow[] {
  const named = (code: string): LocalisedName => opts.regionNames?.get(code) ?? {}
  const base = {
    source_id: rec.id,
    country_code: opts.country,
    kind: opts.kind,
    name: localiseName(rec.name),
    start_date: rec.startDate,
    // Verbatim. Inclusive. Converted only at the edges, by toPeriod().
    end_date: rec.endDate,
    nationwide: rec.nationwide === true,
    ...(opts.syncedAt ? { synced_at: opts.syncedAt } : {}),
  }

  if (opts.queriedGroup) {
    return [{
      ...base, region_code: opts.queriedGroup, region_kind: 'group',
      region_name: named(opts.queriedGroup),
    }]
  }
  if (rec.subdivisions?.length) {
    return rec.subdivisions.map((s) => ({
      ...base, region_code: s.code, region_kind: 'subdivision' as const, region_name: named(s.code),
    }))
  }
  if (rec.groups?.length) {
    return rec.groups.map((g) => ({
      ...base, region_code: g.code, region_kind: 'group' as const, region_name: named(g.code),
    }))
  }
  // The sentinel gets the country's own name ("Tschechien"), when the caller supplied
  // one — a legend reading "* Sommerferien" tells the landlord nothing.
  return [{
    ...base, region_code: NATIONWIDE, region_kind: 'nationwide', region_name: named(NATIONWIDE),
  }]
}

/** Drops duplicate `(source_id, region_code)` pairs — the table's primary key. */
export function dedupeRows(rows: HolidayRow[]): HolidayRow[] {
  const byKey = new Map<string, HolidayRow>()
  for (const r of rows) byKey.set(`${r.source_id}|${r.region_code}`, r)
  return [...byKey.values()]
}

// ------------------------------------------------------------- inclusive -> [from,to)

/** Converts a stored row into the half-open period the calendar works with. */
export function toPeriod(row: HolidayRow | TenantHolidayRow): HolidayPeriod {
  return {
    sourceId: row.source_id,
    country: row.country_code,
    region: row.region_code,
    regionName: row.region_name ?? {},
    kind: row.kind,
    name: row.name ?? {},
    from: row.start_date,
    // Trust the generated column when the row came from the database; fall back to
    // computing it, so a row built in a test or by the sync behaves identically.
    to: row.end_date_excl ?? exclusiveEnd(row.end_date),
    ...('role' in row && row.role ? { role: row.role } : {}),
  }
}

export function toPeriods(rows: Array<HolidayRow | TenantHolidayRow>): HolidayPeriod[] {
  return rows.map(toPeriod).sort((a, b) => a.from.localeCompare(b.from) || a.region.localeCompare(b.region))
}

/** Half-open overlap. A holiday ending the day before `from` does not count. */
export function periodOverlaps(p: HolidayPeriod, from: ISODate, to: ISODate): boolean {
  return p.from < to && from < p.to
}

export function periodCoversDate(p: HolidayPeriod, date: ISODate): boolean {
  return p.from <= date && date < p.to
}

export function periodsOnDate(periods: HolidayPeriod[], date: ISODate): HolidayPeriod[] {
  return periods.filter((p) => periodCoversDate(p, date))
}

/** Groups periods by region so the calendar can draw one lane per market. */
export function groupByRegion(periods: HolidayPeriod[]): Map<string, HolidayPeriod[]> {
  const out = new Map<string, HolidayPeriod[]>()
  for (const p of periods) {
    const key = `${p.country}:${p.region}`
    const list = out.get(key)
    if (list) list.push(p)
    else out.set(key, [p])
  }
  return out
}

/**
 * Collapses touching or overlapping periods of the same region into single bands.
 *
 * Touching counts: Christmas holidays that end on 6 January and a "day off" on
 * 7 January are one continuous closure to a guest, and drawing them as two bands with
 * a hairline gap is a lie about the week.
 */
export function mergeBands(periods: HolidayPeriod[]): Array<{ from: ISODate; to: ISODate; periods: HolidayPeriod[] }> {
  const sorted = [...periods].sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to))
  const out: Array<{ from: ISODate; to: ISODate; periods: HolidayPeriod[] }> = []
  for (const p of sorted) {
    const last = out[out.length - 1]
    if (last && p.from <= last.to) {
      if (p.to > last.to) last.to = p.to
      last.periods.push(p)
    } else {
      out.push({ from: p.from, to: p.to, periods: [p] })
    }
  }
  return out
}

/**
 * How many nights of `[arrival, departure)` fall inside any of these holidays. This is
 * the number that answers "is this a peak week?" — one holiday night in a seven-night
 * stay is not a peak week, six are.
 */
export function holidayNights(periods: HolidayPeriod[], arrival: ISODate, departure: ISODate): number {
  let count = 0
  for (let d = arrival; d < departure; d = addDaysISO(d, 1)) {
    if (periods.some((p) => periodCoversDate(p, d))) count++
  }
  return count
}

// --------------------------------------------------------------- "not yet published"

export type CoverageVerdict = 'covered' | 'not_published' | 'never_published'

/**
 * Distinguishes "no holiday that week" from "nobody has published that year yet".
 *
 * This is not defensive padding. As of the 2026 sync, Italy publishes nothing at all
 * from 2027, and Poland's 2027 has winter breaks but no summer holiday. A calendar
 * that renders both as empty tells an Italian-market landlord their August is
 * off-peak, which is the single most expensive thing this feature could get wrong.
 */
export function coverageFor(c: HolidayCoverage, date: ISODate): CoverageVerdict {
  if (!c.published_through || c.records === 0) return 'never_published'
  return date <= c.published_through ? 'covered' : 'not_published'
}

/** Regions whose data runs out before `date` — what the UI should warn about. */
export function unpublishedRegions(coverage: HolidayCoverage[], date: ISODate): HolidayCoverage[] {
  return coverage.filter((c) => coverageFor(c, date) !== 'covered')
}
